import { editorViewCtx } from '@milkdown/core'
import type { Ctx } from '@milkdown/ctx'
import { TextSelection } from '@milkdown/prose/state'
import type { EditorState } from '@milkdown/prose/state'
import type { Node as ProseNode, NodeType } from '@milkdown/prose/model'
import { MDAST_JINJA_TOKEN, jinjaTokenSchema } from './jinjaToken'
import { isCompleteToken } from './tokenRegex'

/**
 * Render the OPENING delimiter of a Jinja token form (`{{`, `{%`, `{#`, `{`) as
 * a text glyph. Crepe's `Icon` DOMPurify-sanitizes + `innerHTML`s the string
 * into its `.milkdown-icon` span, so a `<span>` (flex-centered + sized to 24px
 * in index.css) aligns with the other icons reliably — SVG `<text>` baseline
 * metrics made the glyph sit high and oversized.
 */
function jinjaGlyph(glyph: string): string {
  return `<span class="top-bar-jinja-glyph">${glyph}</span>`
}

/**
 * One formatting-toolbar button per Jinja token form. `icon` shows the opening
 * delimiter only (`{{`, `{%`, `{#`, `{`); `open`/`close` are the delimiters
 * wrapped around a selection (or inserted empty). All four forms pad with a
 * space, so a wrapped selection reads `{{ patient_name }}` / `{ patient_name }`
 * and an empty insert is a whitespace shell with the caret between the delimiters.
 */
export const JINJA_TOOLBAR_ITEMS = [
  {
    key: 'jinja-expression',
    icon: jinjaGlyph('{{'),
    open: '{{ ',
    close: ' }}',
  },
  { key: 'jinja-statement', icon: jinjaGlyph('{%'), open: '{% ', close: ' %}' },
  { key: 'jinja-comment', icon: jinjaGlyph('{#'), open: '{# ', close: ' #}' },
  { key: 'jinja-fact', icon: jinjaGlyph('{'), open: '{ ', close: ' }' },
] as const

/** The first jinja-token node inside [from, to), with its document position. */
function findJinjaTokenInRange(
  state: EditorState,
  from: number,
  to: number
): { node: ProseNode; pos: number } | null {
  let hit: { node: ProseNode; pos: number } | null = null
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (!hit && node.type.name === MDAST_JINJA_TOKEN) hit = { node, pos }
  })
  return hit
}

/**
 * Identify which form a token value is and pull out its inner content. Forms are
 * matched by their delimiters in `JINJA_TOOLBAR_ITEMS` order (so `{{ }}` wins
 * over `{ }`); `open` is returned so a caller can compare it against the clicked
 * button's `open` to tell "same form" (toggle off) from "different form" (switch).
 */
function detectForm(value: string): { open: string; inner: string } | null {
  for (const item of JINJA_TOOLBAR_ITEMS) {
    const openDelim = item.open.trim()
    const closeDelim = item.close.trim()
    if (
      value.length >= openDelim.length + closeDelim.length &&
      value.startsWith(openDelim) &&
      value.endsWith(closeDelim)
    ) {
      const inner = value
        .slice(openDelim.length, value.length - closeDelim.length)
        .trim()
      return { open: item.open, inner }
    }
  }
  return null
}

/**
 * Build a jinja-token node from `value`, or null when it wouldn't round-trip:
 * the serializer emits the token text verbatim, but the parser only re-tokenizes
 * complete tokens, so e.g. the fact form `{ }` around `patient name` yields
 * `{patient name}` (whitespace) that reloads as plain prose. Callers that wrap /
 * switch existing content go through here; the empty-insert path deliberately
 * skips it, minting an incomplete `{{ }}` with the caret between the delimiters.
 *
 * Known edge: tokens whose inner content holds a brace (e.g. `{% set d = {} %}`)
 * can't wrap/switch — the tokenizer's `{{ }}`/`{# #}`/`{ }` arms stop at the
 * first `}`, so they'd fail round-trip and no-op. Widen those arms if that edge
 * matters; today it fails safe (leaves the existing token untouched).
 */
function createRoundTrippableToken(
  type: NodeType,
  value: string
): ProseNode | null {
  return isCompleteToken(value)
    ? type.create(null, type.schema.text(value))
    : null
}

/**
 * Strip any leading/trailing token delimiters (and their padding) off a plain-text
 * selection before wrapping, so wrapping already-delimited text doesn't
 * double-wrap: `{fact}` → `fact`, `{{ foo }}` → `foo`, a half-typed `{foo` → `foo`.
 * Form-agnostic (the clicked button supplies the new open/close); internal text
 * is untouched, so plain `foo` is a no-op.
 */
function stripTokenDelimiters(text: string): string {
  return text
    .trim()
    .replace(/^\{+[#%]?\s*/, '')
    .replace(/\s*[#%]?\}+$/, '')
    .trim()
}

/**
 * The `open` delimiter of the jinja token in the current selection, or null when
 * the selection holds no token. Drives the toolbar's active-state: a form button
 * lights up when the selection sits on a token of that form (so clicking it again
 * visibly toggles the token off). The toolbar compares this to each button's own
 * `open`.
 */
export function selectionJinjaForm(ctx: Ctx): string | null {
  const view = ctx.get(editorViewCtx)
  const { from, to, empty, $from } = view.state.selection
  // Caret INSIDE a token (empty selection): report that token's form so the
  // toolbar lights up while editing the token text.
  if (empty) {
    return $from.parent.type.name === MDAST_JINJA_TOKEN
      ? (detectForm($from.parent.textContent)?.open ?? null)
      : null
  }
  const existing = findJinjaTokenInRange(view.state, from, to)
  if (!existing) return null
  return detectForm(existing.node.textContent)?.open ?? null
}

/**
 * Insert / wrap / toggle a jinja token, mirroring how bold & italic behave:
 *
 *  - Empty selection → insert an empty token and drop a COLLAPSED caret between
 *    the delimiters, so the user types only the variable name.
 *  - Selection (or node-selection) on an existing token → TOGGLE: clicking the
 *    SAME form unwraps it back to its inner text; clicking a DIFFERENT form
 *    switches the delimiters while keeping the inner content.
 *  - Selection over plain text → flatten the range to plain text (the token holds
 *    no marks/structure) and wrap it in the token.
 */
export function insertOrWrapJinjaToken(
  ctx: Ctx,
  open: string,
  close: string
): void {
  const view = ctx.get(editorViewCtx)
  if (!view.editable) return
  const { state } = view
  const type = jinjaTokenSchema.type(ctx)
  const { from, to, empty } = state.selection

  // Replace [a,b) with `node`, then land a COLLAPSED caret just AFTER it — the
  // token must NOT be left selected (a selection spanning an inline node paints
  // solid via `::selection`, the blue-pill bug). `node.nodeSize` = content + 2.
  const replaceCaretAfter = (a: number, b: number, node: ProseNode): void => {
    const tr = state.tr.replaceWith(a, b, node)
    tr.setSelection(TextSelection.create(tr.doc, a + node.nodeSize))
    view.dispatch(tr)
  }

  // Toggle (same form → unwrap/delete) or switch (different form → re-wrap) an
  // existing token at [pos, pos + node.nodeSize). Shared by a range selection ON
  // a token and a collapsed caret INSIDE one.
  const toggleOrSwitch = (node: ProseNode, pos: number): void => {
    const value = node.textContent
    const form = detectForm(value)
    const inner = form ? form.inner : value
    const start = pos
    const end = pos + node.nodeSize
    if (form && form.open === open) {
      // Same form clicked → unwrap to plain text (or remove when empty).
      view.dispatch(
        inner
          ? state.tr.replaceWith(start, end, state.schema.text(inner))
          : state.tr.delete(start, end)
      )
    } else {
      // Different form → re-wrap the inner content; skip if it won't round-trip
      // (leaving the token in its current form), but still restore editor focus.
      const switched = createRoundTrippableToken(
        type,
        `${open}${inner}${close}`
      )
      if (switched) replaceCaretAfter(start, end, switched)
    }
    view.focus()
  }

  try {
    if (empty) {
      // Caret INSIDE an existing token → toggle/switch it (matches the toolbar
      // active state). `replaceSelectionWith` here would SPLIT the token (its
      // content is `text*`, can't hold a nested token) into malformed halves.
      const { $from } = state.selection
      if ($from.parent.type.name === MDAST_JINJA_TOKEN) {
        toggleOrSwitch($from.parent, $from.before($from.depth))
        return
      }
      const value = `${open}${close}`
      const node = type.create(null, state.schema.text(value))
      if (!node) return
      const tr = state.tr.replaceSelectionWith(node)
      // Caret between the delimiters (content starts at from+1, then open.length
      // chars in) so the user types only the variable name. No inline editor.
      tr.setSelection(TextSelection.create(tr.doc, from + 1 + open.length))
      view.dispatch(tr)
      view.focus()
      return
    }

    const existing = findJinjaTokenInRange(state, from, to)
    if (existing) {
      toggleOrSwitch(existing.node, existing.pos)
      return
    }

    const text = state.doc.textBetween(from, to, '', '')
    // Strip any delimiters the selection already carries so wrapping delimited
    // text (`{fact}`, `{{ foo }}`) re-wraps the inner name, never double-wraps.
    const inner = stripTokenDelimiters(text)
    const node = createRoundTrippableToken(type, `${open}${inner}${close}`)
    if (node) replaceCaretAfter(from, to, node)
    view.focus()
  } catch (error) {
    // replaceWith can throw on selections that don't admit an inline node (e.g.
    // spanning block boundaries). Wrapping is an inline-text affordance; no-op
    // rather than crash the toolbar.
    console.debug('insertOrWrapJinjaToken: skipped (invalid selection)', error)
  }
}
